/**
 * Device Status Utility
 * Determines device online/offline status based on last reading timestamp
 */

import { DeviceInfo } from '../common/enums'
import { MARKER_COLORS, STATUS_CLASSES } from '../config/markerColors'

/**
 * Device online status threshold in minutes
 * Devices are considered online if their last reading is within this timeframe
 */
export const DEVICE_ONLINE_THRESHOLD_MINUTES = 360

/**
 * Device status type
 */
export type DeviceStatus = 'online' | 'offline' | 'pending' | 'config'

/**
 * Device status response from API
 * Used for caching and API responses
 */
export interface DeviceStatusResponse {
  status: DeviceStatus
  lastReadingTimestamp?: number
}

/**
 * Get device status based on last reading timestamp
 *
 * @param device - Device information object
 * @param thresholdMinutes - Minutes threshold for considering device online (default: 360)
 * @returns Device status: 'online', 'offline', or 'pending'
 *
 * Logic:
 * - online: Last reading within threshold minutes
 * - offline: Last reading older than threshold minutes
 * - pending: No last reading timestamp available
 */
export function getDeviceStatus(
  device: DeviceInfo,
  thresholdMinutes: number = DEVICE_ONLINE_THRESHOLD_MINUTES
): DeviceStatus {
  // Type assertion to access potentially undefined lastReadingTimestamp
  const deviceWithTimestamp = device as DeviceInfo & { lastReadingTimestamp?: number | string | Date }

  // Check if device has a last reading timestamp
  if (!deviceWithTimestamp.lastReadingTimestamp) {
    return 'pending'
  }

  try {
    // Parse timestamp - handle multiple formats
    let lastReadingTime: number

    if (typeof deviceWithTimestamp.lastReadingTimestamp === 'number') {
      // Unix timestamp (seconds)
      lastReadingTime = deviceWithTimestamp.lastReadingTimestamp * 1000
    } else if (typeof deviceWithTimestamp.lastReadingTimestamp === 'string') {
      // ISO string or date string
      lastReadingTime = new Date(deviceWithTimestamp.lastReadingTimestamp).getTime()
    } else if (deviceWithTimestamp.lastReadingTimestamp instanceof Date) {
      // Date object
      lastReadingTime = deviceWithTimestamp.lastReadingTimestamp.getTime()
    } else {
      return 'pending'
    }

    // Validate parsed timestamp
    if (isNaN(lastReadingTime) || lastReadingTime <= 0) {
      return 'pending'
    }

    // Calculate time difference in milliseconds
    const currentTime = Date.now()
    const timeDifferenceMs = currentTime - lastReadingTime

    // Convert threshold to milliseconds
    const thresholdMs = thresholdMinutes * 60 * 1000

    // Determine status
    if (timeDifferenceMs < 0) {
      // Future timestamp - invalid, treat as pending
      return 'pending'
    } else if (timeDifferenceMs < thresholdMs) {
      return 'online'
    } else {
      return 'offline'
    }
  } catch (error) {
    console.warn('Error parsing device timestamp:', error)
    return 'pending'
  }
}

/**
 * Get status indicator color class for Tailwind
 *
 * @param status - Device status
 * @returns Tailwind color class (e.g., 'text-green-500')
 */
export function getStatusColorClass(status: DeviceStatus): string {
  if (status === 'config') return 'text-gray-300'
  return STATUS_CLASSES[status]?.text || STATUS_CLASSES.pending.text
}

/**
 * Get status indicator background color class for Tailwind
 *
 * @param status - Device status
 * @returns Tailwind background color class (e.g., 'bg-green-500')
 */
export function getStatusBgColorClass(status: DeviceStatus): string {
  if (status === 'config') return 'bg-gray-300'
  return STATUS_CLASSES[status]?.bg || STATUS_CLASSES.pending.bg
}

/**
 * Get Google Maps marker color based on device status
 *
 * @param status - Device status
 * @returns Hex color code for Google Maps marker (e.g., '#22c55e')
 *
 * Colors are configured in src/config/markerColors.ts
 */
export function getMarkerColor(status: DeviceStatus): string {
  if (status === 'config') return '#d1d5db'
  const color = MARKER_COLORS[status] || MARKER_COLORS.pending

  // Debug logging in development mode
  if (process.env.NODE_ENV === 'development') {
    // Only log occasionally to avoid console spam
    if (Math.random() < 0.01) {
      console.log(`[deviceStatus] getMarkerColor: status="${status}" -> color="${color}"`)
    }
  }

  return color
}
